import { useCallback } from "react";
import { usePermissions } from "@/rbac/usePermissions";

/**
 * Per-status RBAC from the role wizard's per-stage "Status" block.
 *
 * Stored on the role as
 * `stages[].permissions.status_management[String(statusId)]` with two
 * meaningful values: "allow" | "deny". A MISSING entry means ALLOWED —
 * roles saved before this feature carry no `status_management` block and
 * keep every status. Only an explicit "deny" hides a status from pickers
 * and blocks moving leads/deals onto it. Mirrors `StatusPolicy.canSelect`
 * on the server.
 *
 * Read paths (labels, badges, activity, existing rows) must NOT use this —
 * an entity already sitting in a denied status still displays it. Only
 * filter SELECTABLE options, pinning the entity's current status where the
 * picker doubles as the display of the current value.
 */
export function useStatusRbac() {
  const { accessConfig, bypassesRbacChecks } = usePermissions();

  const isStatusDeniedByRole = useCallback(
    (stageId: number | string, statusId: number | string): boolean => {
      if (bypassesRbacChecks) return false;
      const sid = Number(stageId);
      if (!Number.isFinite(sid)) return false;
      const entry = accessConfig?.stages?.find(
        (s: any) => Number(s?.stageId) === sid
      );
      const ctrl = (entry?.permissions as any)?.status_management?.[
        String(statusId)
      ];
      return ctrl === "deny";
    },
    [accessConfig, bypassesRbacChecks]
  );

  /**
   * Filters a stage's status list down to what the role may pick.
   * `pinnedStatusIds` (the entity's current/draft status) always survive
   * so a row already sitting in a denied status keeps displaying it.
   * Falls back to the UNFILTERED list when every status is denied and
   * nothing is pinned — an empty picker would strand the user, and the
   * server still 403s a denied landing.
   */
  const filterRoleAllowedStatuses = useCallback(
    (
      stageId: number | string,
      statuses: any[],
      pinnedStatusIds: Array<number | string | null | undefined> = []
    ): any[] => {
      if (!Array.isArray(statuses) || statuses.length === 0) return statuses;
      const pinned = new Set(
        pinnedStatusIds
          .filter((v) => v != null && v !== "")
          .map((v) => String(v))
      );
      const allowed = statuses.filter((st) => {
        const id = st?.id;
        return pinned.has(String(id)) || !isStatusDeniedByRole(stageId, id);
      });
      return allowed.length > 0 ? allowed : statuses;
    },
    [isStatusDeniedByRole]
  );

  return { isStatusDeniedByRole, filterRoleAllowedStatuses };
}
